import type React from 'react';
import { Loader2, Mic } from 'lucide-react';

import { useVoiceSearch } from '@/hooks/use-voice-search';
import { cn } from '@/lib/utils';

const EQ_BARS = [0.55, 1, 0.78, 0.45];

/**
 * On-device voice search mic. Tap to listen: the button expands open a status
 * pill ("Start speaking…" → "Listening…" → "Transcribing…") with a live
 * equalizer that reacts to your voice. It stops on a short silence (or tap
 * again), transcribes locally, and types the result into the search via
 * `onResult`. First use downloads a small model — shown as a progress ring.
 */
export function VoiceSearchButton({
  onResult,
  className,
}: {
  onResult: (transcript: string) => void;
  className?: string;
}): React.JSX.Element | null {
  const { supported, state, progress, level, heard, error, start, stop } =
    useVoiceSearch({ onResult });

  if (!supported) return null;

  const listening = state === 'listening';
  const transcribing = state === 'transcribing';
  const downloading = transcribing && progress > 0 && progress < 100;
  const open = listening || transcribing;

  const label = downloading
    ? `Loading model… ${progress}%`
    : transcribing
      ? 'Transcribing…'
      : heard
        ? 'Listening…'
        : 'Start speaking…';

  return (
    <span className="relative inline-flex flex-shrink-0 items-center">
      {/* expanding status pill (grows open from the mic) */}
      <span
        aria-hidden={!open}
        className={cn(
          'flex items-center gap-1.5 overflow-hidden whitespace-nowrap text-xs font-medium transition-all duration-300 ease-out',
          listening ? 'text-red-500' : 'text-primary',
          open ? 'max-w-[180px] pl-1 pr-1.5 opacity-100' : 'max-w-0 opacity-0'
        )}
      >
        {transcribing ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <span className="flex h-3.5 items-end gap-[2px]">
            {EQ_BARS.map((m, i) => (
              <span
                key={`eq-${i}`}
                className="w-[2.5px] rounded-full bg-current"
                style={{
                  height: `${Math.max(22, Math.min(100, (0.18 + level * m) * 100))}%`,
                  transition: 'height 90ms linear',
                }}
              />
            ))}
          </span>
        )}
        <span>{label}</span>
      </span>

      <span className="relative inline-flex">
        {/* live mic-level ring while listening */}
        {listening && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 rounded-full bg-red-500/30"
            style={{ transform: `scale(${1 + level * 0.6})`, opacity: 0.5 }}
          />
        )}
        {/* model-download progress ring (first use) */}
        {downloading && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -inset-0.5 rounded-full"
            style={{
              background: `conic-gradient(var(--primary, #a855f7) ${progress * 3.6}deg, transparent 0)`,
              mask: 'radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 2px))',
              WebkitMask:
                'radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 2px))',
            }}
          />
        )}
        <button
          type="button"
          onClick={() => (listening ? stop() : start())}
          disabled={transcribing}
          aria-label={listening ? 'Stop voice search' : 'Search by voice'}
          aria-pressed={listening}
          title={error ? `${error} — tap to retry` : label}
          className={cn(
            'relative inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors',
            listening
              ? 'bg-red-500/15 text-red-500'
              : transcribing
                ? 'text-primary'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            className
          )}
        >
          {transcribing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Mic className={cn('h-4 w-4', listening && 'animate-pulse')} />
          )}
        </button>
      </span>
    </span>
  );
}

export default VoiceSearchButton;
